import { useEffect, useState } from 'react';
import {
  countsUrl,
  onExportSeasonChange,
  selectedExportSeason,
  EXPORT_SCHOOL_CHANGED,
} from '../utils/exportSchoolFilter';

// Shown while the server is still computing a card set. Every key any card
// set reads, so nothing renders blank; the values are the dots.
export const COMPUTING = Object.fromEntries(
  ['ready_for_export', 'not_yet_shipped', 'missing_images', 'shipped_24_hours', 'reprint', 'on_hold', 'work_orders']
    .map((key) => [key, '…']),
);
export const POLL_MS = 4000;

// The figures for one metric-card set (school / seniors / other items), from
// the counts endpoint given. ONE implementation for the three card components,
// so the school filter, the Season box and the computing/poll contract cannot
// drift between them.
//
// The figures come from a cache the server fills in the background (see
// ExportService::OrderPackageCounts). Cold, the server answers "computing" and
// we show dots and poll until the numbers land. Counts must match what the
// card's export would produce, so they are refetched when the school or the
// season changes.
export default function useOrderPackageCounts(endpoint) {
  const [counts, setCounts] = useState([]);
  // '' = all schools. ExportSchoolFilter is a separate React root, so the
  // selection arrives as a DOM event rather than a prop.
  const [schoolId, setSchoolId] = useState('');
  // The Season box: blank = every season.
  const [season, setSeason] = useState(selectedExportSeason());

  useEffect(() => {
    const handleSchoolChange = (e) => setSchoolId((e.detail && e.detail.schoolId) || '');

    document.addEventListener(EXPORT_SCHOOL_CHANGED, handleSchoolChange);
    const unsubscribeSeason = onExportSeasonChange(setSeason);

    return () => {
      document.removeEventListener(EXPORT_SCHOOL_CHANGED, handleSchoolChange);
      unsubscribeSeason();
    };
  }, []);

  useEffect(() => {
    let active = true;
    let timer = null;

    const fetchCounts = async () => {
      let response = await fetch(countsUrl(endpoint, schoolId));
      response = await response.json();
      if (!active) return;
      if (response.computing) {
        setCounts(COMPUTING);
        timer = setTimeout(fetchCounts, POLL_MS);
      } else {
        setCounts(response.data);
      }
    };

    fetchCounts();
    return () => { active = false; clearTimeout(timer); };
  }, [endpoint, schoolId, season]);

  return counts;
}
